import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import DatePicker from 'react-datepicker';
import { format, isSameDay, set } from 'date-fns';
import hu from 'date-fns/locale/hu';

import Field from '../Form/Field';

import '../../../../scss/external/datepicker.scss';

const PublishingTimer = ({ entity, onChange, name, readOnly, ...props }) => {
    const initialDate = entity?.[name] ? new Date(entity[name]) : null;
    const now = new Date();

    const [enabled, setEnabled] = useState(!!initialDate && initialDate > now);
    const [date, setDate] = useState(
        initialDate && initialDate > now ? initialDate : null,
    );

    const isTimerLocked = !!entity?.is_active;

    useEffect(() => {
        if (!isTimerLocked) return;

        if (enabled || date) {
            setEnabled(false);
            setDate(null);
            onChange({ [name]: null });
        }
    }, [isTimerLocked]);

    const handleToggle = (event) => {
        const { checked } = event.target;
        setEnabled(checked);

        if (!checked) {
            setDate(null);
            onChange({ [name]: null });
            return;
        }

        const startDate = new Date();
        setDate(startDate);

        onChange({
            [name]: format(startDate, 'yyyy-MM-dd HH:mm:ss'),
            is_active: false,
        });
    };

    const handleDateChange = (selectedDate) => {
        if (!selectedDate) return;

        const currentNow = new Date();
        if (selectedDate <= currentNow) return;

        setDate(selectedDate);
        onChange({
            [name]: format(selectedDate, 'yyyy-MM-dd HH:mm:ss'),
            is_active: false,
        });
    };

    const minTime = isSameDay(date || now, now)
        ? now
        : set(now, {
              hours: 0,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
          });

    const maxTime = set(now, {
        hours: 23,
        minutes: 59,
        seconds: 59,
        milliseconds: 999,
    });

    return (
        <fieldset className="border-2 border-medium-sky rounded-xl bg-blue-50 p-4">
            <legend className="px-2 text-xl font-semibold text-medium-sky">
                Article timing
            </legend>

            <Field {...props} name={name} unlabeled>
                {() => (
                    <>
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={enabled}
                                disabled={readOnly || isTimerLocked}
                                onChange={handleToggle}
                            />
                            <span className="text-xl">Schedule article</span>
                        </label>

                        {enabled && !isTimerLocked && (
                            <div className="mt-4">
                                <DatePicker
                                    selected={date}
                                    onChange={handleDateChange}
                                    dateFormat="yyyy-MM-dd HH:mm"
                                    showTimeSelect
                                    timeFormat="HH:mm"
                                    timeIntervals={10}
                                    locale={hu}
                                    className="react-datepicker__input"
                                    minDate={now}
                                    minTime={minTime}
                                    maxTime={maxTime}
                                    disabled={readOnly || isTimerLocked}
                                />
                            </div>
                        )}
                    </>
                )}
            </Field>
        </fieldset>
    );
};

PublishingTimer.propTypes = {
    entity: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    name: PropTypes.string,
    readOnly: PropTypes.bool,
};

PublishingTimer.defaultProps = {
    name: 'published_at',
    readOnly: false,
};

export default PublishingTimer;
